import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProductBrands,
  updateProductBrand,
  createProductBrand,
  deleteProductBrand,
} from "../../api/product/brand";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
};

export const defaultController = {
  filters: [],
  page: 0,
  per_page: 50,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteProductBrandAsync = createAsyncThunk(
  "product_brands/deleteProductBrandAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteProductBrand(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_product_brand");
    }
  }
);

export const createProductBrandAsync = createAsyncThunk(
  "product_brands/createProductBrandAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProductBrand(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_product_brand");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_product_brand");
    }
  }
);

export const updateProductBrandAsync = createAsyncThunk(
  "product_brands/updateProductBrandAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductBrand(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_product_brand");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_product_brand");
    }
  }
);

export const getProductBrands = createAsyncThunk(
  "product_brands/getProductBrands",
  async (controller = defaultController, { rejectWithValue }) => {
    try {
      const response = await fetchProductBrands(controller);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_product_brands");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_product_brands");
    }
  }
);

const productBrandsSlice = createSlice({
  name: "product_brands",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductBrands.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProductBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProductBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente produktmerkene.");
      })
      .addCase(updateProductBrandAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProductBrandAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateProductBrandAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere produktmerket.");
      })
      .addCase(createProductBrandAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createProductBrandAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createProductBrandAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette produktmerket.");
      })
      .addCase(deleteProductBrandAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteProductBrandAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (Array.isArray(action.payload)) {
            draftState.data = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProductBrandAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette produktmerket.");
      });
  },
});

export const { reducer } = productBrandsSlice;

export default productBrandsSlice;
